import { FunctionComponent, useContext, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

// One-time reveal of the single-use backup codes, shown right after authenticator
// setup or a regeneration. They cannot be retrieved again, so the copy stresses
// saving them now.
export const BackupCodesDialog: FunctionComponent<{
  open: boolean,
  codes: string[],
  onClose: () => void,
}> = ({ open, codes, onClose }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [copied, setCopied] = useState(false);

  const asText = codes.join('\n');

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      userNotificationsContext.openSnackbar({ open: true, message: 'Backup codes copied' });
    } catch (err) {
      userNotificationsContext.openSnackbar({ open: true, message: 'Could not copy. Select the codes and copy them manually.', backgroundColor: 'text.primary' });
    }
  };

  const downloadCodes = () => {
    const blob = new Blob([`${asText}\n`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'backup-codes.txt';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title="Save your backup codes"
      cancelHandler={onClose}
      submitHandler={onClose}
      isSubmitting={false}
      hideCancelButton
      submitButtonText="Done"
    >
      <Typography variant="body1" sx={{ mb: 2 }}>
        Each code can be used once if you cannot reach your authenticator app. Save them somewhere safe now: you will not be able to see them again.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          p: 2,
          mb: 2,
          borderRadius: 2,
          backgroundColor: 'grey.100',
          fontFamily: 'monospace',
        }}
      >
        {codes.map((code) => (
          <Typography key={code} sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '0.05em' }}>
            {code}
          </Typography>
        ))}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" onClick={copyCodes} startIcon={<ContentCopyOutlined fontSize="small" />}>
          <Typography sx={{ pl: 0.5 }}>{copied ? 'Copied' : 'Copy'}</Typography>
        </Button>
        <Button variant="outlined" onClick={downloadCodes} startIcon={<FileDownloadOutlined fontSize="small" />}>
          <Typography sx={{ pl: 0.5 }}>Download</Typography>
        </Button>
      </Stack>
    </DialogWrapper>
  );
};
